import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPreview = ({ member, settings, onClose }) => {
  const [selectedDevice, setSelectedDevice] = useState('mobile');
  const [notificationType, setNotificationType] = useState('task');

  const devices = [
    { id: 'mobile', label: 'Mobile', icon: 'smartphone' },
    { id: 'desktop', label: 'Desktop', icon: 'monitor' },
    { id: 'tablet', label: 'Tablet', icon: 'tablet' }
  ];

  const notificationTypes = [
    { id: 'task', label: 'Task Reminder', icon: 'check-square' },
    { id: 'calendar', label: 'Calendar Event', icon: 'calendar' },
    { id: 'message', label: 'Message Alert', icon: 'message-square' }
  ];

  const getPreviewContent = () => {
    switch (notificationType) {
      case 'task':
        return {
          title: 'Task Reminder',
          body: 'Pick up groceries - Due in 15 minutes',
          time: 'Now'
        };
      case 'calendar':
        return {
          title: 'Calendar Event',
          body: 'Family Dinner at 7:00 PM - Starting in 1 hour',
          time: 'Now'
        };
      case 'message':
        return {
          title: 'New Message',
          body: 'Sarah: Don\'t forget to pick up milk!',
          time: 'Now'
        };
      default:
        return { title: '', body: '', time: '' };
    }
  };

  const preview = getPreviewContent();

  const renderMobilePreview = () => (
    <div className="bg-gray-900 rounded-3xl p-4 shadow-2xl max-w-sm mx-auto">
      <div className="bg-white rounded-2xl overflow-hidden">
        {/* Notification */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
              style={{ backgroundColor: member?.color }}
            >
              <Icon name="bell" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-900 text-sm">Starling Family Planner</span>
                <span className="text-xs text-gray-500">{preview?.time}</span>
              </div>
              <p className="font-medium text-gray-900 text-sm mb-1">{preview?.title}</p>
              <p className="text-sm text-gray-600 line-clamp-2">{preview?.body}</p>
            </div>
          </div>
        </div>

        {/* Preview indicators */}
        <div className="p-3 bg-gray-50 flex items-center justify-center gap-4 text-xs text-gray-500">
          {settings?.messaging?.sound && notificationType === 'message' && (
            <div className="flex items-center gap-1">
              <Icon name="volume-2" size={12} />
              <span>Sound</span>
            </div>
          )}
          {settings?.tasks?.sound && notificationType === 'task' && (
            <div className="flex items-center gap-1">
              <Icon name="volume-2" size={12} />
              <span>Sound</span>
            </div>
          )}
          {settings?.calendar?.sound && notificationType === 'calendar' && (
            <div className="flex items-center gap-1">
              <Icon name="volume-2" size={12} />
              <span>Sound</span>
            </div>
          )}
          {settings?.messaging?.vibration && notificationType === 'message' && (
            <div className="flex items-center gap-1">
              <Icon name="smartphone" size={12} />
              <span>Vibrate</span>
            </div>
          )}
          {settings?.tasks?.vibration && notificationType === 'task' && (
            <div className="flex items-center gap-1">
              <Icon name="smartphone" size={12} />
              <span>Vibrate</span>
            </div>
          )}
          {settings?.calendar?.vibration && notificationType === 'calendar' && (
            <div className="flex items-center gap-1">
              <Icon name="smartphone" size={12} />
              <span>Vibrate</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDesktopPreview = () => (
    <div className="bg-gray-100 rounded-lg p-8 max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
              style={{ backgroundColor: member?.color }}
            >
              <Icon name="bell" size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">Starling Family Planner</span>
                <span className="text-xs text-gray-500">{preview?.time}</span>
              </div>
              <p className="font-medium text-gray-900 mb-1">{preview?.title}</p>
              <p className="text-sm text-gray-600">{preview?.body}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabletPreview = () => (
    <div className="bg-gray-900 rounded-2xl p-6 shadow-2xl max-w-lg mx-auto">
      <div className="bg-white rounded-xl overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center text-white flex-shrink-0"
              style={{ backgroundColor: member?.color }}
            >
              <Icon name="bell" size={24} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900 text-lg">Starling Family Planner</span>
                <span className="text-sm text-gray-500">{preview?.time}</span>
              </div>
              <p className="font-medium text-gray-900 text-base mb-2">{preview?.title}</p>
              <p className="text-gray-600">{preview?.body}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-h2 font-heading text-foreground mb-2">
                Notification Preview
              </h2>
              <p className="text-sm text-muted-foreground">
                Preview how notifications will appear for {member?.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="x" size={24} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 space-y-6">
          {/* Device Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Device
            </label>
            <div className="flex gap-3">
              {devices?.map(device => (
                <button
                  key={device?.id}
                  onClick={() => setSelectedDevice(device?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    selectedDevice === device?.id
                      ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon name={device?.icon} size={20} />
                  <span className="font-medium">{device?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notification Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Notification Type
            </label>
            <div className="flex gap-3">
              {notificationTypes?.map(type => (
                <button
                  key={type?.id}
                  onClick={() => setNotificationType(type?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                    notificationType === type?.id
                      ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon name={type?.icon} size={20} />
                  <span className="font-medium text-sm">{type?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Preview
            </label>
            <div className="bg-muted/30 rounded-lg p-8">
              {selectedDevice === 'mobile' && renderMobilePreview()}
              {selectedDevice === 'desktop' && renderDesktopPreview()}
              {selectedDevice === 'tablet' && renderTabletPreview()}
            </div>
          </div>

          {/* Settings Info */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">Active Settings</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Delivery: {settings?.deliveryMethod?.join(', ')}</li>
                  <li>• Priority: {settings?.priority}</li>
                  <li>• Quiet hours: {settings?.quietHoursEnabled ? 'Enabled' : 'Disabled'}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end">
          <Button onClick={onClose}>
            Close Preview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreview;